import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Sparkles, GraduationCap } from "lucide-react";
import { trackClick, tutoringUrl } from "../lib/api";

/**
 * SEO-friendly landing page that gathers both offerings on one page:
 *   1. The self-paced Lift Up Reading app ($4/mo, $35/yr).
 *   2. One-on-one virtual tutoring from Lift Up Literacy LLC.
 *
 * We update <title> and <meta description> on mount so search engines and
 * social previews get accurate copy without needing a separate SSR setup.
 */
export default function Bundle() {
    useEffect(() => {
        const prevTitle = document.title;
        document.title = "The bundle: adult reading app + virtual tutoring · Lift Up Reading";
        const meta =
            document.querySelector('meta[name="description"]') ||
            (() => {
                const m = document.createElement("meta");
                m.setAttribute("name", "description");
                document.head.appendChild(m);
                return m;
            })();
        const prevDesc = meta.getAttribute("content");
        meta.setAttribute(
            "content",
            "Learn to read as an adult with the Lift Up Reading app plus optional one-on-one virtual tutoring from Lift Up Literacy LLC. The Science of Reading, on your terms.",
        );
        return () => {
            document.title = prevTitle;
            if (prevDesc != null) meta.setAttribute("content", prevDesc);
        };
    }, []);

    return (
        <main className="container-page py-16 md:py-24 fade-up" data-testid="bundle-page">
            <div className="max-w-3xl">
                <p className="label-eyebrow mb-4">The bundle · read at any age</p>
                <h1
                    className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.1] text-[#1C2321] mb-6"
                    style={{ fontFamily: "Outfit" }}
                    data-testid="bundle-title"
                >
                    An app <span className="text-[#2D5D4E]">and</span> a real
                    teacher, on the same team.
                </h1>
                <p className="text-lg sm:text-xl text-[#515B57] leading-relaxed">
                    Lift Up Reading is the daily practice — five minutes of the Science of Reading,
                    designed for adults. <strong className="text-[#1C2321]">Lift Up Literacy LLC</strong> is
                    the coach on your shoulder — weekly video sessions with a certified reading
                    teacher. Together they cover both muscles: repetition and accountability.
                </p>
            </div>

            {/* Two-column offer comparison */}
            <section className="mt-16 grid md:grid-cols-2 gap-6">
                <div
                    className="card-rw p-8 md:p-10 flex flex-col"
                    style={{ borderTop: "6px solid #2D5D4E" }}
                    data-testid="bundle-card-app"
                >
                    <div className="w-12 h-12 rounded-lg bg-[#E0EDE6] flex items-center justify-center mb-5">
                        <Sparkles className="w-6 h-6 text-[#2D5D4E]" strokeWidth={1.6} />
                    </div>
                    <p className="label-eyebrow mb-1">Self-paced</p>
                    <h2
                        className="text-2xl font-medium mb-3 text-[#1C2321]"
                        style={{ fontFamily: "Outfit" }}
                    >
                        Lift Up Reading (app)
                    </h2>
                    <p className="text-[#515B57] leading-relaxed mb-6">
                        36 short lessons across the five pillars, six practice games, and a patient
                        AI tutor available around the clock. Built for adults — no cartoons, no shame.
                    </p>
                    <ul className="space-y-2.5 text-[#1C2321] mb-8">
                        {[
                            "5-minute daily path, adapts to you",
                            "Words read aloud on tap",
                            "Free forever — no card, no trial timer",
                        ].map((line) => (
                            <li key={line} className="flex items-start gap-2 leading-relaxed">
                                <Check
                                    className="w-5 h-5 text-[#2D5D4E] mt-0.5 flex-shrink-0"
                                    strokeWidth={2}
                                />
                                <span>{line}</span>
                            </li>
                        ))}
                    </ul>
                    <Link
                        to="/register"
                        onClick={() => trackClick("bundle", "internal:register")}
                        data-testid="bundle-cta-register"
                        className="mt-auto inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] text-lg font-medium hover:bg-[#1F4237] transition-all hover:-translate-y-0.5 self-start"
                    >
                        Start the app — it's free
                        <ArrowRight className="w-5 h-5" strokeWidth={1.8} />
                    </Link>
                </div>

                <div
                    className="card-rw p-8 md:p-10 flex flex-col"
                    style={{ borderTop: "6px solid #D47A55" }}
                    data-testid="bundle-card-tutoring"
                >
                    <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center mb-5"
                        style={{ background: "#FBE7DA" }}
                    >
                        <GraduationCap className="w-6 h-6 text-[#D47A55]" strokeWidth={1.6} />
                    </div>
                    <p className="label-eyebrow mb-1" style={{ color: "#D47A55" }}>
                        With a real teacher
                    </p>
                    <h2
                        className="text-2xl font-medium mb-3 text-[#1C2321]"
                        style={{ fontFamily: "Outfit" }}
                    >
                        Lift Up Literacy LLC (tutoring)
                    </h2>
                    <p className="text-[#515B57] leading-relaxed mb-6">
                        Weekly one-on-one video lessons with a certified reading teacher. Structured
                        Orton-Gillingham-informed instruction. Real accountability. Ideal alongside
                        the app or on its own.
                    </p>
                    <ul className="space-y-2.5 text-[#1C2321] mb-8">
                        {[
                            "Certified reading specialists",
                            "Weekly video sessions, homework included",
                            "Custom pace for adults and students",
                        ].map((line) => (
                            <li key={line} className="flex items-start gap-2 leading-relaxed">
                                <Check
                                    className="w-5 h-5 text-[#D47A55] mt-0.5 flex-shrink-0"
                                    strokeWidth={2}
                                />
                                <span>{line}</span>
                            </li>
                        ))}
                    </ul>
                    <a
                        href={tutoringUrl("bundle")}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackClick("bundle", "liftupliteracy.com")}
                        data-testid="bundle-cta-tutoring"
                        className="mt-auto inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#D47A55] text-white text-lg font-medium hover:bg-[#B86644] transition-all hover:-translate-y-0.5 self-start"
                    >
                        Explore tutoring
                        <ArrowRight className="w-5 h-5" strokeWidth={1.8} />
                    </a>
                </div>
            </section>

            {/* Why-together strip */}
            <section
                className="mt-16 bg-white border border-[#E5E7E2] rounded-2xl p-10 md:p-14"
                data-testid="bundle-why-together"
            >
                <p className="label-eyebrow mb-3">Why they work together</p>
                <h2
                    className="text-3xl font-medium text-[#1C2321] mb-8 max-w-2xl"
                    style={{ fontFamily: "Outfit" }}
                >
                    Reading is a habit, not an event.
                </h2>
                <div className="grid md:grid-cols-3 gap-8">
                    {[
                        {
                            title: "Practice, every day",
                            body:
                                "The app fills the six days you're not in a lesson. Short, forgiving, portable.",
                        },
                        {
                            title: "A person who knows you",
                            body:
                                "Your tutor sees the gaps live and moves the plan. The app follows through.",
                        },
                        {
                            title: "One learner, one voice",
                            body:
                                "Same warm tone, same evidence-based method. No mixed signals, no shame.",
                        },
                    ].map((item) => (
                        <div key={item.title}>
                            <h3
                                className="text-lg font-medium mb-2 text-[#1C2321]"
                                style={{ fontFamily: "Outfit" }}
                            >
                                {item.title}
                            </h3>
                            <p className="text-[#515B57] leading-relaxed">{item.body}</p>
                        </div>
                    ))}
                </div>
            </section>
        </main>
    );
}
